import { useEffect, useMemo, useState } from 'react';
import roleService from 'services/role';

/**
 * What the signed-in admin's role lets them open, and how to filter a menu by it.
 *
 * Both halves come from the server. The permission a menu section needs is not
 * hard-coded here: the catalogue endpoint returns the `menuIds` each permission
 * covers, so the dashboard and the API cannot drift into disagreeing about
 * which screens belong to "Repairs".
 *
 * While it is loading, and if the request fails, everything is shown. A sidebar
 * that hides itself because a request was slow is worse than one that briefly
 * offers a link the server will refuse — and the server is the thing actually
 * enforcing this, so the worst case of being wrong here is an error message
 * rather than access.
 */
const useAdminPermissions = () => {
  const [state, setState] = useState({
    loading: true,
    unrestricted: true,
    permissions: [],
    byMenuId: new Map(),
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([roleService.mine(), roleService.permissions()])
      .then(([mineRes, catalogueRes]) => {
        if (cancelled) return;
        const mine = mineRes?.data ?? {};
        const catalogue = Array.isArray(catalogueRes?.data) ? catalogueRes.data : [];
        const byMenuId = new Map();
        catalogue.forEach((entry) =>
          (entry.menuIds ?? []).forEach((id) => byMenuId.set(id, entry.key)),
        );
        setState({
          loading: false,
          unrestricted: mine.unrestricted !== false,
          permissions: Array.isArray(mine.permissions) ? mine.permissions : [],
          byMenuId,
        });
      })
      .catch(() => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const { loading, unrestricted, permissions, byMenuId } = state;

    /** True when this top-level menu entry should be offered. */
    const allows = (menuId) => {
      if (loading || unrestricted) return true;
      const needed = byMenuId.get(menuId);
      /* A section nobody has classified stays visible, matching the server,
         which does not guard a route prefix it does not know about. */
      if (!needed) return true;
      return permissions.includes(needed);
    };

    const filterMenu = (list) =>
      Array.isArray(list) ? list.filter((item) => allows(item?.id)) : list;

    return { loading, unrestricted, permissions, allows, filterMenu };
  }, [state]);
};

export default useAdminPermissions;
